import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type CountSheetPayload, type ValidationReport } from '../api/client';
import { generateCountSheet, parseCountSheet } from '../lib/countsheet';

// Count Sheet (§5.6 / §5.7). One click generates the branded Symbol Quantity
// Excel; the human fills QTY and drops it back. Re-import matches by hidden Row ID
// and returns a validation report. The software NEVER counts.
export function CountSheet() {
  const { id = '' } = useParams();
  const [data, setData] = useState<CountSheetPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try { setData(await api.getCountSheet(id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function generate() {
    if (!data) return;
    setBusy('Generating branded Excel…'); setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const blob = await generateCountSheet(data.project, data.rows, today);
      const safe = data.project.name.replace(/[^\w.-]+/g, '_').slice(0, 60) || 'project';
      downloadBlob(blob, `${safe}_Symbol_Quantity_Count_Sheet.xlsx`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Generate failed'); }
    finally { setBusy(''); }
  }

  async function importFile(file: File) {
    setBusy(`Reading ${file.name}…`); setError(''); setReport(null);
    try {
      const rows = await parseCountSheet(file);
      setBusy('Validating and importing…');
      const res = await api.importCounts(id, rows);
      setReport(res.report);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Import failed'); }
    finally { setBusy(''); if (fileRef.current) fileRef.current.value = ''; }
  }

  const rows = data?.rows ?? [];
  const counted = rows.filter((r) => r.count != null).length;

  return (
    <div onKeyDown={(e) => { if (e.key === 'Enter' && fileRef.current) fileRef.current.click(); }}>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← Scope Analysis</Link>
      <h1 style={{ margin: '4px 0 0' }}>Count Sheet — {data?.project?.name ?? 'Project'}</h1>
      <p className="muted" style={{ marginTop: 4 }}>
        Generate the branded Symbol Quantity Excel, enter quantities by hand, then drop it back.
        <strong> The software never counts — every quantity is yours.</strong>
      </p>

      {error && <div className="error">{error}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
        {/* Generate */}
        <div className="card">
          <h2>1 · Generate Symbol Quantity Excel</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {rows.length} symbol{rows.length === 1 ? '' : 's'} in scope (selected systems, excluding mapping-excluded).
            {counted > 0 && <> · {counted} already have counts (they round-trip).</>}
          </p>
          <button className="btn btn-gold" disabled={!rows.length || !!busy} onClick={generate}>
            {busy.startsWith('Generating') ? busy : 'Generate Symbol Quantity Excel'}
          </button>
          {!rows.length && <p className="muted" style={{ marginBottom: 0 }}>No symbols yet — analyze documents and resolve mappings first.</p>}
        </div>

        {/* Import */}
        <div className="card">
          <h2>2 · Import completed sheet</h2>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) void importFile(f); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--teal)' : 'var(--gray-300)'}`, borderRadius: 8,
              padding: '22px 16px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--teal-light)' : 'var(--gray-050)',
            }}
          >
            {busy && !busy.startsWith('Generating') ? <span>{busy}</span>
              : <span className="muted">Drop the completed <strong>.xlsx</strong> here, or click to choose. Press <kbd>Enter</kbd> to browse.</span>}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); }} />
        </div>
      </div>

      {/* Validation report (§9) */}
      {report && <ReportCard report={report} />}

      {report?.accepted ? (
        <div className="card" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div><strong>Counts imported.</strong> Next: cascade them into the internal BOM.</div>
          <Link className="btn btn-primary btn-sm" to={`/projects/${id}/bom`}>Generate BOM →</Link>
        </div>
      ) : null}

      {/* Current counts */}
      {rows.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>Symbols in scope</h2>
          <table>
            <thead><tr><th>System</th><th>Symbol</th><th>Description</th><th>Device type</th><th>Current QTY</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.symbol_id}>
                  <td className="muted">{r.system_name ?? '—'}</td>
                  <td><strong>{r.symbol_tag ?? '—'}</strong></td>
                  <td>{r.legend_description ?? '—'}</td>
                  <td>{r.device_type ?? <span style={{ color: 'var(--danger)' }}>needs mapping</span>}</td>
                  <td>{r.count != null ? r.count : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report }: { report: ValidationReport }) {
  const problems: [string, string[]][] = [
    ['Unmatched row IDs', report.unmatched],
    ['Non-numeric quantities', report.non_numeric],
    ['Negative quantities', report.negatives],
    ['Duplicate symbols', report.duplicates],
    ['Missing / blank quantities', report.blanks],
  ];
  return (
    <div className={`card ${report.ok ? '' : ''}`} style={{ marginTop: 16, borderColor: report.ok ? 'var(--teal)' : 'var(--danger)' }}>
      <h2>Import validation report</h2>
      <div className={`banner ${report.ok ? '' : 'banner-danger'}`}>
        {report.ok
          ? <><strong>Clean import.</strong> {report.accepted} of {report.total_symbols} symbols counted with no issues.</>
          : <><strong>Imported with issues.</strong> {report.accepted} of {report.total_symbols} accepted — resolve the flags below, then re-import.</>}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>Rows received: {report.rows_received} · matched by hidden Row ID (never by position).</p>
      <table>
        <tbody>
          {problems.map(([label, items]) => (
            <tr key={label}>
              <td style={{ whiteSpace: 'nowrap' }}><strong>{label}</strong></td>
              <td>{items.length === 0 ? <span className="muted">none</span>
                : <span style={{ color: 'var(--danger)' }}>{items.length}: {items.slice(0, 20).join(', ')}{items.length > 20 ? '…' : ''}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}
